import { useState, useMemo, useCallback } from 'react';
import { useGetListVehicleAcceptanceFormQuery } from '../../../../../api/endpoints/vehicleAcceptenceForm';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import ChassisMultiSelect from '../../../../../components/common/ChassisDropdownWithMultiSelect';
import CountryMultiSelect from '../../../../../components/common/CountryDropdownWithMultiSelect';
import DealerDropdown from '../../../../../components/common/DealerDropdownWithMulti';


export const useVehicleAcceptanceFormList = (th, currentStatus) => {
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        sortField: null,
        sortOrder: null
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [apiFilters, setApiFilters] = useState({});
    const { getCurrentLanguageId } = useLanguage();



    // Remove status from filter fields since it's now controlled by tabs
    const filterFields = useMemo(
        () => [
            {
                field: 'startDate',
                headerName: th('common.startDate'),
                type: 'date'
            },
            {
                field: 'endDate',
                headerName: th('common.endDate'),
                type: 'date'
            },
           {
                field: 'dealerId',
                headerName: th('common.dealer'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <DealerDropdown 
                        value={value} 
                        onChange={onChange}
                        className="w-full"
                        multiple={true}
                        rowCount={100}
                    />
                )
            },
         {
                field: 'country',
                headerName: th('warranty.vehicleAcceptanceForm.filters.country'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <CountryMultiSelect 
                        value={value}
                        onChange={onChange}
                        className="w-full"
                    />
                )
            },
            {
                field: 'chassisNo',
                headerName: th('warranty.vehicleAcceptanceForm.filters.chassisNo'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <ChassisMultiSelect 
                        value={value} 
                        onChange={onChange} 
                        className="w-full" 
                        showDescription={false}
                    />
                )
            },
            {
                field: 'vehicleAcceptanceStartDate',
                headerName: th('warranty.vehicleAcceptanceForm.filters.vehicleAcceptanceStartDate'),
                type: 'date'
            },
            {
                field: 'vehicleAcceptanceEndDate',
                headerName: th('warranty.vehicleAcceptanceForm.filters.vehicleAcceptanceEndDate'),
                type: 'date'
            }
        ],
        [th]
    );

    const queryParams = useMemo(() => {
        const activeFilters = { ...apiFilters };

        return {
            PageNumber: lazyParams.page + 1,
            PageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            Status: currentStatus,
            ...activeFilters,
            ...(searchTerm ? { searchTerm } : {})
        };
    }, [lazyParams, apiFilters, searchTerm, currentStatus, getCurrentLanguageId]);

    const { data: vehicleAcceptanceForms, isLoading: loading, refetch } = useGetListVehicleAcceptanceFormQuery(queryParams);

    // Client-side sıralama işlemi
    const items = useMemo(() => {
        const data = vehicleAcceptanceForms?.vehicleAcceptanceFormGetListQueryItems || [];

        // Sıralama varsa uygula
        if (lazyParams.sortField && lazyParams.sortOrder !== null) {
            const sortedData = [...data].sort((a, b) => {
                let aValue = a[lazyParams.sortField];
                let bValue = b[lazyParams.sortField];

                // Null değerler için kontrol
                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';

                // Sayısal değerler için kontrol
                if (!isNaN(aValue) && !isNaN(bValue)) {
                    aValue = Number(aValue);
                    bValue = Number(bValue);
                }

                // String değerler için case-insensitive karşılaştırma
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                // Sıralama
                if (aValue < bValue) return lazyParams.sortOrder === 1 ? -1 : 1;
                if (aValue > bValue) return lazyParams.sortOrder === 1 ? 1 : -1;
                return 0;
            });

            return sortedData;
        }

        return data;
    }, [vehicleAcceptanceForms, lazyParams.sortField, lazyParams.sortOrder]);

    const totalRecords = vehicleAcceptanceForms?.totalCount || 0;
    const tabCounts = useMemo(() => {
        const map = {};
        (vehicleAcceptanceForms?.statusCounts ?? []).forEach((i) => {
            map[i.status] = i.count;
        });
        return map;
    }, [vehicleAcceptanceForms]);

    const onPage = (event) => {
        setLazyParams(prev => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: event.page
        }));
    };

    const onSort = (event) => {
        setLazyParams(prev => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    // Arama işlevi
    const handleSearch = useCallback((value) => {
        setSearchTerm(value);
        // Reset to first page when searching
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    }, []);

  const handleApplyFilters = useCallback((updatedFilters) => {
    const newApiFilters = {};
    Object.entries(updatedFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            // Tarih alanları
            if ((key === 'vehicleAcceptanceStartDate' || 
                 key === 'vehicleAcceptanceEndDate' || 
                 key === 'startDate' || 
                 key === 'endDate') && value instanceof Date) {
                const selectedDate = new Date(value);
                const timezoneOffset = selectedDate.getTimezoneOffset();
                const adjustedDate = new Date(selectedDate.getTime() - (timezoneOffset * 60000));
                newApiFilters[key] = adjustedDate.toISOString();
            }
            // DealerId için çoklu seçim desteği
            else if (key === 'dealerId') {
                if (value.isAllDealers) {
                    newApiFilters['dealerIds'] = [];
                } else if (value.dealerIds && value.dealerIds.length > 0) {
                    newApiFilters['dealerIds'] = value.dealerIds;
                }
            }
            // Country için çoklu seçim
            else if (key === 'country') {
                if (Array.isArray(value) && value.length > 0) {
                    newApiFilters['countries'] = value.map(c => 
                        typeof c === 'string' ? c : c.name || c.text || c.displayValue
                    );
                }
                else if (typeof value === 'object' && value.name) {
                    newApiFilters['countries'] = [value.name];
                }
                else if (typeof value === 'string') {
                    newApiFilters['countries'] = [value];
                }
            }
            // ChassisNo için çoklu seçim
            else if (key === 'chassisNo') {
                if (typeof value === 'string' && value.trim() !== '') {
                    newApiFilters['chassisNos'] = [value];
                }
                else if (Array.isArray(value) && value.length > 0) {
                    newApiFilters['chassisNos'] = value;
                }
            }
            else {
                newApiFilters[key] = value;
            }
        }
    });

    setApiFilters(newApiFilters);
    setLazyParams(prev => ({
        ...prev,
        first: 0,
        page: 0
    }));
}, []);

    const handleResetFilters = useCallback(() => {
        setApiFilters({});
        setSearchTerm('');
        // Reset to first page when resetting filters
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    }, []);

    return {
        lazyParams,
        setLazyParams,
        items,
        totalRecords,
        tabCounts,
        loading,
        refetch,
        onPage,
        onSort,
        filterFields,
        handleSearch,
        handleApplyFilters,
        handleResetFilters,
        queryParams 
    };
};